import { useCallback } from 'react';
import keyBy from 'lodash/keyBy';
import { useService } from '@atlassian/jira-common-services/src/use-service';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import { useServiceDeskContext } from '@atlassian/jira-servicedesk-spa-commons/src/common/utils/servicedesk-context/index.tsx';
import type { ProjectKey } from '@atlassian/jira-shared-types/src/general.tsx';
import { PACKAGE_NAME, TEAM_NAME, WIDGET_SERVICE_API_BASE_URL } from '../../common/constants';
import type { FormsResponse, ReturnObject } from './types';

export const getForms = async (
	serviceDeskId: number,
	projectId: number,
): Promise<FormsResponse | undefined> =>
	performGetRequest(
		`${WIDGET_SERVICE_API_BASE_URL}/forms?serviceDeskId=${serviceDeskId}&projectId=${projectId}`,
	).catch((error: Error) => {
		fireErrorAnalytics({
			meta: {
				id: 'getForms',
				packageName: PACKAGE_NAME,
				teamName: TEAM_NAME,
			},
			error,
			sendToPrivacyUnsafeSplunk: true,
		});
		throw error;
	});

export const useGetForms = (projectKey: ProjectKey, projectId: number): ReturnObject => {
	const [{ data: serviceDeskContext }] = useServiceDeskContext(projectKey);
	const { serviceDeskId } = serviceDeskContext || {};
	const request = useCallback(
		() => (serviceDeskId ? getForms(serviceDeskId, projectId) : Promise.resolve(undefined)),
		[projectId, serviceDeskId],
	);
	const { loading, error, data, fetch } = useService<FormsResponse>(request);
	return [{ loading, error, data: data ? keyBy(data.forms, 'id') : undefined }, fetch];
};
